import { normalizeImportedName } from "@/lib/deck-import";
import type { InventoryEntry } from "@/lib/local-store";
import type { CardRecord } from "@/lib/types";

export type ImportRow = {
  name?: string;
  setCode?: string;
  number?: string;
  quantity: number;
  printNumber?: string;
  expansion?: string;
  raw?: string;
};

export type MatchedImport = {
  row: ImportRow;
  card: CardRecord | null;
  cardId: string | null;
  quantity: number;
  status: "matched" | "unmatched" | "skipped";
  reason?: string;
};

export type ImportPreview = {
  matched: MatchedImport[];
  unmatched: MatchedImport[];
  skipped: MatchedImport[];
  totalQuantity: number;
};

function normalizeHeader(h: string): string {
  return h
    .trim()
    .toLowerCase()
    .replace(/^\ufeff/, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === "," || ch === ";") {
      cells.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  cells.push(cur.trim());
  return cells;
}

const NAME_KEYS = new Set(["name", "card_name", "cardname", "product_name", "productname", "nom"]);
const QTY_KEYS = new Set(["quantity", "qty", "count", "copies", "qte", "quantite", "amount"]);
const SET_KEYS = new Set(["set", "set_code", "setcode", "set_id", "setid", "expansion_code", "expansioncode"]);
const NUM_KEYS = new Set([
  "number",
  "collector_number",
  "collectornumber",
  "card_number",
  "cardnumber",
  "print_number",
  "printnumber",
  "num",
]);
const PRINT_KEYS = new Set(["printnumber", "print_number", "print"]);
const EXP_KEYS = new Set(["expansion", "set_name", "setname", "expansion_name"]);

/** Parse un CSV (CardNexus export, inventaire manuel, etc.). */
export function parseCollectionCsv(raw: string): ImportRow[] {
  const text = raw.replace(/^\ufeff/, "").trim();
  if (!text) return [];

  const lines = text.split(/\r?\n/u).filter((l) => l.trim());
  if (lines.length === 0) return [];

  const headerCells = parseCsvLine(lines[0]).map(normalizeHeader);
  const hasHeader = headerCells.some(
    (h) => NAME_KEYS.has(h) || QTY_KEYS.has(h) || SET_KEYS.has(h) || NUM_KEYS.has(h) || PRINT_KEYS.has(h),
  );

  const rows: ImportRow[] = [];

  if (hasHeader) {
    const idx = {
      name: headerCells.findIndex((h) => NAME_KEYS.has(h)),
      qty: headerCells.findIndex((h) => QTY_KEYS.has(h)),
      set: headerCells.findIndex((h) => SET_KEYS.has(h)),
      num: headerCells.findIndex((h) => NUM_KEYS.has(h)),
      print: headerCells.findIndex((h) => PRINT_KEYS.has(h)),
      expansion: headerCells.findIndex((h) => EXP_KEYS.has(h)),
    };

    for (let i = 1; i < lines.length; i++) {
      const cells = parseCsvLine(lines[i]);
      const quantity = idx.qty >= 0 ? Number(cells[idx.qty]?.replace(/[^\d.-]/g, "") || 0) : 1;
      if (!Number.isFinite(quantity) || quantity <= 0) continue;

      const name = idx.name >= 0 ? cells[idx.name] : undefined;
      const setCode = idx.set >= 0 ? cells[idx.set] : undefined;
      const number = idx.num >= 0 ? cells[idx.num] : undefined;
      const printNumber = idx.print >= 0 ? cells[idx.print] : undefined;
      const expansion = idx.expansion >= 0 ? cells[idx.expansion] : undefined;

      if (!name && !setCode && !number && !printNumber) continue;

      rows.push({
        name: name || undefined,
        setCode: setCode || undefined,
        number: number || undefined,
        printNumber: printNumber || undefined,
        expansion: expansion || undefined,
        quantity: Math.floor(quantity),
        raw: lines[i],
      });
    }
    return rows;
  }

  // Fallback: "4 Card Name" or "4 OGN 195"
  for (const line of lines) {
    const m = line.trim().match(/^(\d+)\s+(.+)$/u);
    if (!m) continue;
    const quantity = Number(m[1]);
    const rest = m[2].trim();
    const setNum = rest.match(/^([A-Za-z]{2,6})\s*[- ]?\s*(\d+)\b/u);
    if (setNum) {
      rows.push({
        setCode: setNum[1],
        number: setNum[2],
        name: rest,
        quantity,
        raw: line,
      });
    } else {
      rows.push({ name: rest, quantity, raw: line });
    }
  }
  return rows;
}

/** Convertit les lignes JSON d'un export CardNexus. */
export function rowsFromCardnexusExport(items: unknown[]): ImportRow[] {
  const rows: ImportRow[] = [];
  for (const item of items) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const quantity = Number(o.quantity ?? 0);
    if (!Number.isFinite(quantity) || quantity <= 0) continue;
    const game = String(o.game ?? "").toLowerCase();
    if (game && game !== "riftbound") continue;

    const expansionCode =
      o.expansionCode != null
        ? String(o.expansionCode)
        : o.setCode != null
          ? String(o.setCode)
          : undefined;
    const expansionRaw = o.expansion;
    const expansion =
      typeof expansionRaw === "string"
        ? expansionRaw
        : expansionRaw && typeof expansionRaw === "object" && "code" in (expansionRaw as object)
          ? String((expansionRaw as { code?: string }).code ?? "")
          : undefined;

    rows.push({
      name: o.name != null ? String(o.name) : undefined,
      printNumber: o.printNumber != null ? String(o.printNumber) : undefined,
      expansion: expansion || undefined,
      setCode: expansionCode || expansion || undefined,
      number: o.number != null ? String(o.number) : undefined,
      quantity: Math.floor(quantity),
    });
  }
  return rows;
}

function buildIndexes(cards: CardRecord[]) {
  const byName = new Map<string, CardRecord[]>();
  const bySetNum = new Map<string, CardRecord>();
  const byPrint = new Map<string, CardRecord>();

  for (const card of cards) {
    const key = normalizeImportedName(card.name);
    const list = byName.get(key) ?? [];
    list.push(card);
    byName.set(key, list);
    if (card.cleanName) {
      const ck = normalizeImportedName(card.cleanName);
      const cl = byName.get(ck) ?? [];
      cl.push(card);
      byName.set(ck, cl);
    }
    if (card.setId != null && card.collectorNumber != null) {
      const set = card.setId.toUpperCase();
      bySetNum.set(`${set}-${card.collectorNumber}`, card);
      bySetNum.set(`${set}-${String(card.collectorNumber).padStart(3, "0")}`, card);
      byPrint.set(`${set}${card.collectorNumber}`.toLowerCase(), card);
      byPrint.set(`${set}-${card.collectorNumber}`.toLowerCase(), card);
    }
    if (card.riftboundId) {
      byPrint.set(card.riftboundId.toLowerCase().replace(/\s+/g, ""), card);
    }
  }
  return { byName, bySetNum, byPrint };
}

function resolveRow(row: ImportRow, indexes: ReturnType<typeof buildIndexes>): CardRecord | null {
  if (row.printNumber) {
    const compact = row.printNumber.toLowerCase().replace(/\s+/g, "");
    const hit = indexes.byPrint.get(compact) || indexes.byPrint.get(compact.replace(/_/g, "-"));
    if (hit) return hit;

    const m = row.printNumber.match(/^([A-Za-z]{2,6})\s*[- ]?\s*(\d+)/u);
    if (m) {
      const key = `${m[1].toUpperCase()}-${Number(m[2])}`;
      const bySet = indexes.bySetNum.get(key) || indexes.bySetNum.get(`${m[1].toUpperCase()}-${m[2]}`);
      if (bySet) return bySet;
    }
  }

  const setHint = row.setCode || row.expansion;
  if (setHint && row.number) {
    const set = setHint.toUpperCase().replace(/[^A-Z0-9]/g, "");
    const num = String(Number(row.number.replace(/\D/g, "")) || row.number);
    const hit =
      indexes.bySetNum.get(`${set}-${num}`) ||
      indexes.bySetNum.get(`${set}-${String(num).padStart(3, "0")}`);
    if (hit) return hit;
  }

  // printNumber bare + expansion code (CardNexus enrichi)
  if (setHint && row.printNumber && /^\d+$/.test(row.printNumber.trim())) {
    const set = setHint.toUpperCase().replace(/[^A-Z0-9]/g, "");
    const num = Number(row.printNumber);
    const hit =
      indexes.bySetNum.get(`${set}-${num}`) ||
      indexes.bySetNum.get(`${set}-${String(num).padStart(3, "0")}`);
    if (hit) return hit;
  }

  if (row.name) {
    const key = normalizeImportedName(row.name);
    const list = indexes.byName.get(key);
    if (list?.length === 1) return list[0];
    if (list && list.length > 1 && row.setCode) {
      const set = row.setCode.toUpperCase();
      const narrowed = list.find((c) => (c.setId ?? "").toUpperCase() === set);
      if (narrowed) return narrowed;
    }
    if (list?.length) return list[0];

    // Strip set suffix from CardNexus-style names
    const withoutParen = normalizeImportedName(row.name.replace(/\s*\([^)]*\)\s*$/u, ""));
    const list2 = indexes.byName.get(withoutParen);
    if (list2?.length) return list2[0];
  }

  return null;
}

export function matchImportRows(rows: ImportRow[], cards: CardRecord[]): ImportPreview {
  const indexes = buildIndexes(cards);
  const matched: MatchedImport[] = [];
  const unmatched: MatchedImport[] = [];
  const skipped: MatchedImport[] = [];
  let totalQuantity = 0;

  for (const row of rows) {
    if (row.quantity <= 0) {
      skipped.push({ row, card: null, cardId: null, quantity: 0, status: "skipped", reason: "Quantité nulle" });
      continue;
    }
    const card = resolveRow(row, indexes);
    if (!card) {
      unmatched.push({ row, card: null, cardId: null, quantity: row.quantity, status: "unmatched" });
      continue;
    }
    matched.push({ row, card, cardId: card.id, quantity: row.quantity, status: "matched" });
    totalQuantity += row.quantity;
  }

  return { matched, unmatched, skipped, totalQuantity };
}

/** Agrège les quantités par cardId (somme des lignes matchées). */
export function aggregateMatchedInventory(matched: MatchedImport[]): InventoryEntry[] {
  const map = new Map<string, number>();
  for (const m of matched) {
    if (!m.cardId || m.quantity <= 0) continue;
    map.set(m.cardId, (map.get(m.cardId) ?? 0) + m.quantity);
  }
  return [...map.entries()].map(([cardId, quantity]) => ({ cardId, quantity }));
}
